import { fmtDayLong, fmtTime, zoneAbbr } from '@concord/domain';
import type { EventView } from '@concord/domain/contracts';
import type { LogEntry } from '@concord/ui';

const KIND: Record<string, string> = { patient: 'patient', expert: 'physician', guest: 'guest' };

function who(e: EventView): string | undefined {
  if (e.actorKind === 'system') return e.via ?? undefined;
  const base = e.actorName ?? '';
  const role = KIND[e.actorKind];
  const parts = [role ? `${base}, ${role}` : base];
  if (e.via) parts.push(e.via);
  return parts.filter(Boolean).join(' · ') || undefined;
}

const n = (v: unknown) => (typeof v === 'number' ? v : 0);
const str = (v: unknown) => (typeof v === 'string' ? v : '');
const plural = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

/**
 * Plain sentences for the case log (CON-05). The API stores facts; wording lives here so it
 * can change without rewriting history.
 */
/** The engagement terms acknowledged with an acceptance; older entries carry no version. */
const termsLine = (d: Record<string, unknown>) =>
  typeof d.termsVersion === 'string'
    ? `Engagement terms v${d.termsVersion} acknowledged in the acceptance.`
    : 'Terms acknowledged in the acceptance.';

export function describeEvent(e: EventView, tz: string): LogEntry {
  const at = new Date(e.at);
  const base = {
    id: e.id,
    day: fmtDayLong(at, tz),
    time: fmtTime(at, tz),
    zone: zoneAbbr(tz, at),
    who: who(e),
    staffOnly: e.visibility === 'staff',
  };
  const d = e.data;
  switch (e.type) {
    case 'intake.started':
      return { ...base, title: d.onBehalf ? 'Intake started by phone' : 'Intake started' };
    case 'records.added':
      return { ...base, title: `${plural(n(d.count), 'record')} uploaded with descriptions` };
    case 'case.submitted':
      return {
        ...base,
        dot: 'acc',
        title: d.onBehalf ? 'Case submitted on the patient’s behalf' : 'Case submitted',
        detail: `Reference ${str(d.reference)} assigned. ${plural(n(d.times), 'proposed time')}, ${plural(n(d.records), 'record')}, ${plural(n(d.questions), 'question')}. Terms v${str(d.termsVersion)} accepted.`,
      };
    case 'record.added':
      return {
        ...base,
        title: `Record added: ${str(d.description) || str(d.filename)}`,
        detail: d.externalSource ? `Received outside the platform: ${str(d.externalSource)}.` : undefined,
      };
    case 'record.removed':
      return { ...base, title: `Record removed: ${str(d.description) || str(d.filename)}` };
    case 'record.downloaded':
      return { ...base, title: `Downloaded ${str(d.description)}` };
    case 'times.changed':
      return { ...base, title: `Offered times changed to ${plural(n(d.count), 'time')}` };
    case 'invitation.staged':
      return {
        ...base,
        dot: 'wait',
        title: `Invitation staged: ${str(d.expert)} (ID ${n(d.expertPublicId)}), ${str(d.mode)}`,
        detail: d.outOfPlan
          ? `${str(d.expert)} is outside the plan's network. Out-of-plan override recorded.`
          : undefined,
      };
    case 'invitation.unstaged':
      return { ...base, title: `Removed from the panel before release: ${str(d.expert)}` };
    case 'invitation.issued':
      return {
        ...base,
        title: `Invitation issued: ${str(d.expert)} (ID ${n(d.expertPublicId)}), ${str(d.mode)}`,
      };
    case 'case.released': {
      const exp = str(d.expiresAt);
      return {
        ...base,
        dot: 'acc',
        title: 'Case released',
        detail: `${plural(n(d.invitations), 'invitation')} issued with ${plural(n(d.times), 'proposed time')}.${exp ? ` Expire ${fmtDayLong(new Date(exp), tz)}, ${fmtTime(new Date(exp), tz)} ${zoneAbbr(tz, new Date(exp))}.` : ''}`,
      };
    }
    case 'invitations.sent':
      return { ...base, dot: 'acc', title: `${plural(n(d.invitations), 'more invitation')} sent` };
    case 'invitation.accepted': {
      const at = new Date(str(d.startsAt));
      return {
        ...base,
        dot: 'ok',
        title: `${e.actorName ?? 'Physician'} accepted ${fmtDayLong(at, tz)}, ${fmtTime(at, tz)} ${zoneAbbr(tz, at)}`,
        who: e.via ?? undefined,
        detail: d.provisional
          ? `Time recorded as provisional: panel of ${n(d.panelSize)} not yet filled. ${termsLine(d)}`
          : termsLine(d),
      };
    }
    case 'invitation.declined':
      return {
        ...base,
        dot: 'bad',
        title: `${e.actorName ?? 'Physician'} declined`,
        who: e.via ?? undefined,
        detail: d.reason
          ? `Reason given: ${str(d.reason)}. Invitation closed.`
          : 'No reason given. Invitation closed.',
      };
    case 'invitation.alternative_requested':
      return {
        ...base,
        dot: 'alert',
        title: `${e.actorName ?? 'Physician'} asked for another time`,
        who: e.via ?? undefined,
        detail: `“${str(d.constraint)}”`,
      };
    case 'invitation.missed_out':
      return {
        ...base,
        title: `${str(d.expert)} missed out: the panel filled first`,
        detail: 'Not a decline. Told without fault.',
      };
    case 'invitation.expired':
      return { ...base, dot: 'alert', title: `Invitation expired: ${str(d.expert)}` };
    case 'invitation.reset':
      return {
        ...base,
        title: `Invitation clock reset: ${str(d.expert)}`,
        detail: 'Physician notified again with the same link.',
      };
    case 'invitation.reminded':
      return { ...base, title: `Reminder sent: ${str(d.expert)}` };
    case 'invitation.withdrawn':
      return {
        ...base,
        title: `Invitation withdrawn: ${str(d.expert)}`,
        detail: d.reason ? `Reason: ${str(d.reason)}` : undefined,
      };
    case 'panel.size_changed':
      return {
        ...base,
        title: `Panel size set to ${n(d.to)}`,
        detail: `Expected responses ${n(d.expectedResponses)}.`,
      };
    case 'consultation.confirmed': {
      const at = new Date(str(d.startsAt));
      return {
        ...base,
        dot: 'ok',
        title: `Consultation confirmed for ${fmtDayLong(at, tz)}, ${fmtTime(at, tz)} ${zoneAbbr(tz, at)}`,
        detail:
          d.reason === 'panel_filled'
            ? 'The panel filled. Every participant emailed with a calendar file.'
            : 'Every participant emailed with a calendar file.',
      };
    }
    case 'consultation.set': {
      const at = new Date(str(d.startsAt));
      return {
        ...base,
        dot: 'ok',
        title: `Consultation set for ${fmtDayLong(at, tz)}, ${fmtTime(at, tz)} ${zoneAbbr(tz, at)}`,
      };
    }
    case 'consultation.rescheduled': {
      const from = new Date(str(d.from));
      const to = new Date(str(d.to));
      return {
        ...base,
        dot: 'alert',
        title: `Consultation rescheduled to ${fmtDayLong(to, tz)}, ${fmtTime(to, tz)} ${zoneAbbr(tz, to)}`,
        detail: `Was ${fmtDayLong(from, tz)}, ${fmtTime(from, tz)} ${zoneAbbr(tz, from)}. Every participant notified.`,
      };
    }
    case 'note.added':
      return { ...base, note: true, title: str(d.body), who: e.actorName ?? undefined };
    case 'case.held':
      return { ...base, dot: 'alert', title: 'Put on hold', detail: `Reason: ${str(d.reason)}` };
    case 'case.hold_cleared':
      return { ...base, title: 'Hold cleared', detail: d.reason ? `Was: ${str(d.reason)}` : undefined };
    case 'flag.quality_review':
      return { ...base, title: d.on ? 'Flagged for quality review' : 'Quality review flag cleared' };
    case 'flag.prevent_invitations':
      return { ...base, title: d.on ? 'Invitations turned off for this case' : 'Invitations turned back on' };
    case 'setup.changed': {
      const parts = Object.entries(d).map(([k, v]) => {
        const x = v as { from?: string | null; to?: string | null };
        return `${k === 'caseType' ? 'Case type' : 'Network'}: ${x.from ?? 'none'} → ${x.to ?? 'none'}`;
      });
      return { ...base, title: 'Setup changed', detail: parts.join('. ') };
    }
    case 'plan.moved':
      return {
        ...base,
        title: `Plan changed: ${str(d.from)} → ${str(d.to)}`,
        detail: 'The patient moved to another plan, and this case moved with them.',
      };
    case 'guest.added':
      return { ...base, title: `Guest invitee added: ${str(d.name)}` };
    case 'guest.removed':
      return { ...base, title: `Guest removed: ${str(d.name)}` };
    case 'case.canceled':
      return {
        ...base,
        dot: 'bad',
        title: 'Case canceled',
        detail: `Reason: ${str(d.reason)}. Participants notified.`,
      };
    case 'session.completed':
      return { ...base, dot: 'ok', title: 'Consultation marked as held' };
    case 'case.closed':
      return { ...base, dot: 'acc', title: 'Case closed' };
    case 'case.owner_changed':
      return {
        ...base,
        title: `Reassigned to ${str(d.to)}`,
        detail: `${d.from ? `Was ${str(d.from)}` : 'Had no owner'}${d.bulk ? ', handed over with the rest of their open cases' : ''}.`,
      };
    case 'join_link.set':
      return {
        ...base,
        title: d.source === 'zoom' ? 'Zoom meeting made' : 'Join link pasted',
        detail: d.source === 'zoom' ? 'Its link went to everyone on the consultation.' : undefined,
      };
    case 'join_link.failed':
      return {
        ...base,
        dot: 'bad',
        title: `Zoom could not ${d.work === 'delete' ? 'delete' : d.work === 'update' ? 'move' : 'make'} the meeting`,
        detail: 'Every retry failed. Try Zoom again from the consultation panel, or paste a join link.',
      };
    default:
      return { ...base, title: e.type.replace(/[._]/g, ' ') };
  }
}
